/**
 * Syntax colouring for lesson code panels.
 *
 * Deliberately small: the languages a course page shows (JSON, shell, .env,
 * SQL, TypeScript/JavaScript), one pass of regular-expression tokens each.
 * It works on plain text and escapes every character it emits, so the output
 * can only ever contain its own <span class="tok-…"> wrappers.
 */

const escape = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

type Rule = [RegExp, string];

const JS_KEYWORDS =
  'import|export|from|default|const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|new|class|extends|async|await|try|catch|finally|throw|typeof|instanceof|in|of|interface|type|enum|implements|public|private|protected|readonly|as|null|undefined|true|false|this|void';

const RULES: Record<string, Rule[]> = {
  json: [
    [/"(?:[^"\\]|\\.)*"(?=\s*:)/y, 'tok-key'],
    [/"(?:[^"\\]|\\.)*"/y, 'tok-str'],
    [/-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b/y, 'tok-num'],
    [/\b(?:true|false|null)\b/y, 'tok-kw'],
    [/[{}[\],:]/y, 'tok-punc'],
  ],
  shell: [
    [/#.*/y, 'tok-com'],
    [/"(?:[^"\\]|\\.)*"|'[^']*'/y, 'tok-str'],
    [/(?<=^|[\n;&|]\s*)(?:\$\s+)?[\w./-]+/y, 'tok-cmd'],
    [/\s--?[\w-]+/y, 'tok-flag'],
    [/\$\{?\w+\}?/y, 'tok-var'],
    [/&&|\|\||[|;>]/y, 'tok-punc'],
  ],
  env: [
    [/#.*/y, 'tok-com'],
    [/^[A-Za-z_][\w.]*(?==)/my, 'tok-key'],
    [/=/y, 'tok-punc'],
    [/(?<==).*/y, 'tok-str'],
  ],
  sql: [
    [/--.*/y, 'tok-com'],
    [/'(?:[^'\\]|\\.)*'/y, 'tok-str'],
    [
      /\b(?:select|from|where|and|or|not|insert|into|values|update|set|delete|create|table|alter|drop|join|left|right|inner|outer|on|group|by|order|limit|having|as|primary|key|foreign|references|null|distinct|count|with|asc|desc)\b/iy,
      'tok-kw',
    ],
    [/\b\d+(?:\.\d+)?\b/y, 'tok-num'],
  ],
  javascript: [
    [/\/\/.*|\/\*[\s\S]*?\*\//y, 'tok-com'],
    [/`(?:[^`\\]|\\.)*`|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/y, 'tok-str'],
    [new RegExp(`\\b(?:${JS_KEYWORDS})\\b`, 'y'), 'tok-kw'],
    [/\b\d+(?:\.\d+)?\b/y, 'tok-num'],
    [/\b[A-Z][A-Za-z0-9_]*\b/y, 'tok-type'],
    [/\b[a-zA-Z_$][\w$]*(?=\s*\()/y, 'tok-fn'],
  ],
};
RULES.typescript = RULES.javascript!;
RULES.ts = RULES.javascript!;
RULES.js = RULES.javascript!;
RULES.bash = RULES.shell!;
RULES.sh = RULES.shell!;

const LABELS: Record<string, string> = {
  json: 'JSON',
  shell: 'Terminal',
  bash: 'Terminal',
  sh: 'Terminal',
  env: '.env',
  sql: 'SQL',
  typescript: 'TypeScript',
  ts: 'TypeScript',
  javascript: 'JavaScript',
  js: 'JavaScript',
  html: 'HTML',
  css: 'CSS',
  python: 'Python',
  text: 'Code',
};

export function languageLabel(language: string): string {
  return LABELS[language.toLowerCase()] ?? language.toUpperCase();
}

export function highlight(source: string, language: string): string {
  const rules = RULES[language.toLowerCase()];
  if (!rules) return escape(source);
  let out = '';
  let plain = '';
  let i = 0;
  while (i < source.length) {
    let matched = false;
    for (const [pattern, token] of rules) {
      pattern.lastIndex = i;
      const match = pattern.exec(source);
      if (match && match[0].length > 0) {
        out += escape(plain);
        plain = '';
        out += `<span class="${token}">${escape(match[0])}</span>`;
        i += match[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      plain += source[i];
      i += 1;
    }
  }
  return out + escape(plain);
}
